/**
 * API Service for TOTD Admin Panel
 * Handles all API requests with authentication and error handling
 */

// Get API base URL from environment
// const API_BASE_URL = 'http://localhost:5002/api';
const API_BASE_URL = 'https://api.kaustubhagashe.in/api';

// Types
export interface Thought {
  _id: string;
  text: string;
  author: string;
  category: string;
  imageUrl: string;
  scheduledDate: string;
  status: 'draft' | 'scheduled' | 'published' | 'archived';
  isPublished: boolean;
  stats: {
    likes: number;
    comments: number;
    shares: number;
    saves: number;
    views: number;
  };
  tags: string[];
  createdAt: string;
  updatedAt: string;
}

export interface Comment {
  _id: string;
  thoughtId: string;
  userId: {
    _id: string;
    name: string;
    profilePicture: string;
  };
  text: string;
  level: number;
  likes: number;
  moderationStatus: 'pending' | 'approved' | 'rejected' | 'flagged';
  createdAt: string;
  replies?: Comment[];
}

export interface User {
  _id: string;
  phoneNumber: string;
  name: string;
  email?: string;
  profilePicture: string;
  isAdmin: boolean;
  isActive: boolean;
  isSuspended: boolean;
  isBanned: boolean;
  stats: {
    thoughtsRead: number;
    currentStreak: number;
    longestStreak: number;
  };
  preferences: {
    notificationsEnabled: boolean;
    dailyReminder: boolean;
    reminderTime: string;
  };
  createdAt: string;
}

// Helper function to get auth token
const getAuthToken = (): string | null => {
  return localStorage.getItem('admin_token');
};

// Helper function to get headers
const getHeaders = (includeAuth = true): HeadersInit => {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  if (includeAuth) {
    const token = getAuthToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }

  return headers;
};

// Helper function to handle API response
const handleResponse = async <T = any>(response: Response): Promise<T> => {
  const text = await response.text();

  if (!response.ok) {
    let message = 'API request failed';
    try {
      const data = JSON.parse(text);
      // express-validator failures come back as { errors: [{ msg }] } with no
      // top-level message — surface the first one instead of the generic text.
      const validationMsg = Array.isArray(data.errors) ? data.errors[0]?.msg : null;
      message = data.message || validationMsg || data.error?.message || data.error || message;
    } catch {}
    throw new Error(message);
  }

  if (!text) return {} as T;
  return JSON.parse(text) as T;
};

// ==================== AUTH ENDPOINTS ====================

export const authApi = {
  // Admin login with email/password
  login: async (email: string, password: string) => {
    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: getHeaders(false),
      body: JSON.stringify({ email, password }),
    });
    
    const data = await handleResponse(response);

    // Check if user is admin
    if (data.success && data.user && !data.user.isAdmin) {
      throw new Error('Access denied. Admin privileges required.');
    }

    if (data.token) {
      localStorage.setItem('admin_token', data.token);
      if (data.user) {
        localStorage.setItem('admin_user', JSON.stringify(data.user));
      }
    }

    return data;
  },

  // Admin signup with email/password
  signup: async (name: string, email: string, password: string) => {
    const response = await fetch(`${API_BASE_URL}/auth/register`, {
      method: 'POST',
      headers: getHeaders(false),
      body: JSON.stringify({ name, email, password }),
    });
    
    const data = await handleResponse(response);

    if (data.token) {
      localStorage.setItem('admin_token', data.token);
      if (data.user) {
        localStorage.setItem('admin_user', JSON.stringify(data.user));
      }
    }

    return data;
  },

  // Forgot password — emails a 6-digit reset code
  forgotPassword: async (email: string) => {
    const response = await fetch(`${API_BASE_URL}/auth/forgot-password`, {
      method: 'POST',
      headers: getHeaders(false),
      body: JSON.stringify({ email }),
    });
    return handleResponse(response);
  },

  // Reset password using the emailed code
  resetPassword: async (email: string, otp: string, password: string) => {
    const response = await fetch(`${API_BASE_URL}/auth/reset-password`, {
      method: 'POST',
      headers: getHeaders(false),
      body: JSON.stringify({ email, otp, password }),
    });
    return handleResponse(response);
  },

  // Change password (authenticated)
  changePassword: async (currentPassword: string, newPassword: string) => {
    const response = await fetch(`${API_BASE_URL}/auth/change-password`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify({ currentPassword, newPassword }),
    });
    return handleResponse(response);
  },

  // Logout
  logout: () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
  },

  getCurrentUser: async (): Promise<User> => {
    const response = await fetch(`${API_BASE_URL}/auth/me`, {
      headers: getHeaders(),
    });
    const data = await handleResponse(response);
    return data.user;
  },
};

// ==================== ADMIN - THOUGHT MANAGEMENT ====================

export const adminThoughtsApi = {
  getAll: async (params?: {
    status?: string;
    category?: string;
    page?: number;
    limit?: number;
    search?: string;
  }) => {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.category) queryParams.append('category', params.category);
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.search) queryParams.append('search', params.search);

    const response = await fetch(`${API_BASE_URL}/admin/thoughts?${queryParams}`, {
      headers: getHeaders(),
    });
    return handleResponse<{ thoughts: Thought[]; totalPages: number; total: number }>(response);
  },

  create: async (thoughtData: FormData | Record<string, any>) => {
    const token = getAuthToken();
    const isFormData = thoughtData instanceof FormData;
    const response = await fetch(`${API_BASE_URL}/admin/thoughts`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
      },
      body: isFormData ? thoughtData : JSON.stringify(thoughtData),
    });
    return handleResponse(response);
  },

  update: async (id: string, thoughtData: FormData | Record<string, any>) => {
    const token = getAuthToken();
    const isFormData = thoughtData instanceof FormData;
    const response = await fetch(`${API_BASE_URL}/admin/thoughts/${id}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
      },
      body: isFormData ? thoughtData : JSON.stringify(thoughtData),
    });
    return handleResponse(response);
  },

  delete: async (id: string) => {
    const response = await fetch(`${API_BASE_URL}/admin/thoughts/${id}`, {
      method: 'DELETE',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  bulkUpload: async (thoughts: any[]) => {
    const response = await fetch(`${API_BASE_URL}/admin/thoughts/bulk-upload`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ thoughts }),
    });
    return handleResponse(response);
  },

  getLikers: async (id: string) => {
    const response = await fetch(`${API_BASE_URL}/admin/thoughts/${id}/likers`, {
      headers: getHeaders(),
    });
    return handleResponse<{ likers: User[]; total: number }>(response);
  },

  getSavers: async (id: string) => {
    const response = await fetch(`${API_BASE_URL}/admin/thoughts/${id}/savers`, {
      headers: getHeaders(),
    });
    return handleResponse<{ savers: User[]; total: number }>(response);
  },
};

// ==================== ADMIN - COMMENT MODERATION ====================

export const adminCommentsApi = {
  getAll: async (params?: { status?: string; page?: number; limit?: number; thoughtId?: string }) => {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.thoughtId) queryParams.append('thoughtId', params.thoughtId);

    const response = await fetch(`${API_BASE_URL}/admin/comments?${queryParams}`, {
      headers: getHeaders(),
    });
    return handleResponse<{ comments: Comment[]; totalPages: number; total: number }>(response);
  },

  approve: async (id: string) => {
    const response = await fetch(`${API_BASE_URL}/admin/comments/${id}/moderate`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify({ moderationStatus: 'approved' }),
    });
    return handleResponse(response);
  },

  remove: async (id: string) => {
    const response = await fetch(`${API_BASE_URL}/admin/comments/${id}/moderate`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify({ moderationStatus: 'rejected' }),
    });
    return handleResponse(response);
  },

  moderate: async (
    id: string,
    moderationStatus: 'approved' | 'rejected' | 'flagged',
    moderationReason?: string
  ) => {
    const response = await fetch(`${API_BASE_URL}/admin/comments/${id}/moderate`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify({ moderationStatus, moderationReason }),
    });
    return handleResponse(response);
  },

  delete: async (id: string) => {
    const response = await fetch(`${API_BASE_URL}/admin/comments/${id}`, {
      method: 'DELETE',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },
};

// ==================== ADMIN - ANALYTICS ====================

export const adminAnalyticsApi = {
  get: async () => {
    const response = await fetch(`${API_BASE_URL}/admin/analytics`, {
      headers: getHeaders(),
    });
    return handleResponse(response);
  },
};

// ==================== ADMIN - CATEGORIES ====================

export interface Category {
  _id: string;
  name: string;
  description?: string;
  icon?: string;
  color?: string;
  order?: number;
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
}

export const adminCategoriesApi = {
  getAll: async () => {
    const response = await fetch(`${API_BASE_URL}/admin/categories`, {
      headers: getHeaders(),
    });
    return handleResponse<{ categories: Category[] }>(response);
  },

  create: async (data: {
    name: string;
    description?: string;
    icon?: string;
    color?: string;
    order?: number;
  }) => {
    const response = await fetch(`${API_BASE_URL}/admin/categories`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<{ category: Category }>(response);
  },

  update: async (id: string, data: {
    name?: string;
    description?: string;
    icon?: string;
    color?: string;
    order?: number;
  }) => {
    const response = await fetch(`${API_BASE_URL}/admin/categories/${id}`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<{ category: Category }>(response);
  },

  delete: async (id: string) => {
    const response = await fetch(`${API_BASE_URL}/admin/categories/${id}`, {
      method: 'DELETE',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },
};

// ==================== ADMIN - USER MANAGEMENT ====================

export const adminUsersApi = {
  getAll: async (params?: { status?: string; search?: string; page?: number; limit?: number; isAdmin?: boolean }) => {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.search) queryParams.append('search', params.search);
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.isAdmin !== undefined) queryParams.append('isAdmin', params.isAdmin.toString());

    const response = await fetch(`${API_BASE_URL}/admin/users?${queryParams}`, {
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  getById: async (id: string) => {
    const response = await fetch(`${API_BASE_URL}/admin/users/${id}`, {
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  suspend: async (id: string, reason?: string) => {
    const response = await fetch(`${API_BASE_URL}/admin/users/${id}/suspend`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ reason }),
    });
    return handleResponse(response);
  },

  ban: async (id: string, reason?: string) => {
    const response = await fetch(`${API_BASE_URL}/admin/users/${id}/ban`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ reason }),
    });
    return handleResponse(response);
  },

  activate: async (id: string) => {
    const response = await fetch(`${API_BASE_URL}/admin/users/${id}/activate`, {
      method: 'POST',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },
};

// ==================== ADMIN - ANNOUNCEMENTS ====================

export const adminAnnouncementsApi = {
  getAll: async () => {
    const response = await fetch(`${API_BASE_URL}/admin/announcements`, {
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  create: async (data: {
    title: string;
    message: string;
    type?: 'info' | 'warning' | 'success' | 'update';
    status?: 'draft' | 'scheduled' | 'published';
    scheduledDate?: string;
  }) => {
    const response = await fetch(`${API_BASE_URL}/admin/announcements`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  update: async (id: string, data: any) => {
    const response = await fetch(`${API_BASE_URL}/admin/announcements/${id}`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  delete: async (id: string) => {
    const response = await fetch(`${API_BASE_URL}/admin/announcements/${id}`, {
      method: 'DELETE',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },
};

// ==================== ADMIN - SETTINGS ====================

export const adminSettingsApi = {
  get: async () => {
    const response = await fetch(`${API_BASE_URL}/admin/settings`, {
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  update: async (settings: any) => {
    const response = await fetch(`${API_BASE_URL}/admin/settings`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify(settings),
    });
    return handleResponse(response);
  },
};

// ==================== ADMIN - OCCASIONS ====================

export interface Occasion {
  _id: string;
  value: string;
  label: string;
  emoji: string;
  isActive: boolean;
  order: number;
  createdAt: string;
  updatedAt: string;
}

export const adminOccasionsApi = {
  getAll: async () => {
    const response = await fetch(`${API_BASE_URL}/admin/occasions`, {
      headers: getHeaders(),
    });
    return handleResponse<{ occasions: Occasion[] }>(response);
  },

  create: async (data: { value: string; label: string; emoji: string; order?: number }) => {
    const response = await fetch(`${API_BASE_URL}/admin/occasions`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<{ occasion: Occasion }>(response);
  },

  update: async (id: string, data: { label?: string; emoji?: string; order?: number; isActive?: boolean }) => {
    const response = await fetch(`${API_BASE_URL}/admin/occasions/${id}`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<{ occasion: Occasion }>(response);
  },

  delete: async (id: string) => {
    const response = await fetch(`${API_BASE_URL}/admin/occasions/${id}`, {
      method: 'DELETE',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },
};

// Export all APIs
export default {
  auth: authApi,
  adminThoughts: adminThoughtsApi,
  adminComments: adminCommentsApi,
  adminAnalytics: adminAnalyticsApi,
  adminCategories: adminCategoriesApi,
  adminUsers: adminUsersApi,
  adminAnnouncements: adminAnnouncementsApi,
  adminSettings: adminSettingsApi,
  adminOccasions: adminOccasionsApi,
};